/* RESPONSIBLE TEAM: team-data-foundations */
/**
 * Service for caching workflow-connector/insertable-action records to improve performance.
 *
 * Previously, findAll('workflow-connector/insertable-action') was called in 13+ places
 * throughout the app, causing redundant API calls. This service provides centralized
 * caching with manual invalidation when needed.
 *
 * Cache Management:
 * - insertable-action records are mostly read-only data from the server
 * - If you modify these records or know they've changed on the server, call:
 *   - this.workflowConnectorInsertableActions.refetch() - to clear cache and immediately load fresh data
 *
 * Usage:
 * - Routes: await this.workflowConnectorInsertableActions.loadActions()
 * - Components: this.workflowConnectorInsertableActions.actions (sync access to cached data)
 */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type InsertableAction from 'embercom/models/workflow-connector/insertable-action';

export default class WorkflowConnectorInsertableActionsService extends Service {
  @service declare store: Store;

  @tracked _actions: InsertableAction[] | null = null;
  @tracked _loadPromise: Promise<InsertableAction[]> | null = null;

  /**
   * Get cached actions synchronously. Returns empty array if not loaded yet.
   * Use loadActions() first to ensure data is available.
   */
  get actions(): InsertableAction[] {
    return this._actions || [];
  }

  /**
   * Load workflow connector actions with caching.
   * Subsequent calls return the same promise until data is loaded.
   */
  async loadActions(): Promise<InsertableAction[]> {
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._loadPromise = this._fetchActions();
    return this._loadPromise;
  }

  private async _fetchActions(): Promise<InsertableAction[]> {
    try {
      let actions = await this.store.findAll('workflow-connector/insertable-action');
      // Handle both live array and test scenarios where toArray might not exist
      if (actions && typeof actions.toArray === 'function') {
        this._actions = actions.toArray();
      } else if (Array.isArray(actions)) {
        this._actions = actions;
      } else {
        this._actions = [];
      }
      return this._actions;
    } catch (error) {
      // Reset promise on error so it can be retried
      this._loadPromise = null;
      throw error;
    }
  }

  /**
   * Refetch actions by invalidating cache and loading fresh data in background
   * Call this when you know the actions have changed - refetch happens async
   */
  refetch(): void {
    this._actions = null;
    this._loadPromise = null;
    // Fire and forget - load actions in background
    this.loadActions();
  }
}
